import { useState } from 'react';
import { TabBar } from './ui.jsx';

/* ══════════════════════════════════════════════════════════════════════════
 * HELP — `08h_Help.html`
 *
 * ⚠️ EVERY WORD IS QUOTED, NOT WRITTEN. RULE 4. This screen answers questions
 * about how Eleat actually behaves — what a rating means, when a reservation
 * is a reservation, why a review needs a verified visit — and those answers
 * are promises. A reworded promise is a different promise.
 *
 * ⚠️ AND THE ANSWERS MATCH THE CODE, WHICH IS WHY THEY ARE NOT REWRITTEN HERE.
 * "Below five reviews there is no number at all" is `rating_shown`, enforced by
 * a database trigger. "A review needs a verified visit" is `eligibleVisit()`
 * throwing 403 in reviews.service.js. "Until the restaurant responds, it is a
 * request" is the reservation rule in the glossary. If one of these sentences
 * ever stops being true of the code, the fix is the code — or a conversation
 * with Al — never a quiet edit to this page.
 *
 * ⚠️ NO BACKEND, ON PURPOSE. Nothing here is fetched, so Help works when an
 * Eleater is signed out, offline-ish, or stuck at a door that is failing. The
 * one screen somebody opens BECAUSE something is wrong must not be the screen
 * that needs everything to be right.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE FOUR SECTIONS AND THEIR ORDER ARE THE LOCKED SCREEN'S: YOUR ACCOUNT ·
   RESERVATIONS · REVIEWS AND RATINGS · DEALS. */
const SECTIONS = [
  ['YOUR ACCOUNT', [
    /* Al-Noor, 8 Oct 2026: "where are we going to put this… how to pronounce"
       — the sounds are the glossary's (2. Database/eleat_glossary.json,
       `pronounce`), sourced from Wiktionary and Namedrop */
    ['How do you say Eleat?',
      'Eleat sounds like the word “elite”: ih-LEET, spelled E-L-E-A-T. A person using Eleat is an Eleater: ih-LEE-ter. Zaryna, Eleat’s AI, is said zuh-REE-nuh.'],
    ['What is my Eleater ID?',
      'It is how Eleat refers to your account, and it is on your profile. You do not need it day to day — nothing asks you to type it in.'],
    ['How do I change my name, email or number?',
      'In Settings, on your profile. Changing your email or number sends a six-digit code to the new one before it takes effect, so nobody can move your account without you.'],
    ['Why does Eleat ask for my date of birth?',
      'Eleat asks once, when you join, so your account is complete and so we can tell you apart from anyone else with your name. It is not shown on your public profile.'],
    ['Do I need an account to look around?',
      'No. Browsing, searching and every restaurant page are open to everyone. An account is needed the moment you do something — save a place, share it, make a reservation, write a review or ask Zaryna.'],
    ['How do I delete my account?',
      'In Settings, at the bottom. It is permanent — your account cannot be restored and nobody at Eleat can bring it back. It removes your details, saved places, reservations and notifications. Reviews you have published stay up without your name on them, because a restaurant’s rating cannot be rewritten after the fact.'],
  ]],
  ['RESERVATIONS', [
    ['Is my reservation confirmed?',
      'A request is sent to the restaurant and its status shows in Reservations on your profile. Until the restaurant responds, it is a request — Eleat never tells you a table is held when it has not been.'],
    ['How do I cancel or change one?',
      'Open it in Reservations. The restaurant is told straight away. Letting them know early is the difference between a table they can resell and one that sits empty.'],
    ['Can I tell them about an allergy?',
      'Yes — there is a line for it when you reserve, and it reaches the kitchen on its own, not buried in a note. Always confirm it with your server as well.'],
  ]],
  ['REVIEWS AND RATINGS', [
    ['Why can I not review this restaurant?',
      'A review needs a verified visit — Eleat has to know you were actually there. That is the whole reason a rating on Eleat means something, and it is why we will not open it up.'],
    ['What is my Eleat rating?',
      'Restaurants rate you after a visit, the same way you rate them. It is anonymous, you only ever see your own average, and it appears once three restaurants have rated you.'],
    ['How is a restaurant’s rating worked out?',
      'The average of its eligible reviews. Below five reviews there is no number at all — an average of two opinions is not a rating, and showing one would mislead you.'],
  ]],
  ['DEALS', [
    ['What is the difference between the deals?',
      'Happy Hour runs to a set schedule each week. Daily Deals are on today. Last-Minute Specials are on for a few hours. Game Day is tied to a game the venue is showing. The Signature Menu is not a deal — it is what the restaurant is known for.'],
    ['Why is a deal I saw yesterday gone?',
      'Deals are set by the restaurant and most run to a window. When the window closes the deal comes off the app rather than sitting there out of date.'],
  ]],
];

export default function Help() {
  /* ⚠️ ONE OPEN AT A TIME, AND ALL SHUT TO BEGIN WITH — the locked screen's
     own shape. Fourteen answers opened at once is a wall of text nobody reads;
     the questions are the index. */
  const [open, setOpen] = useState(null);

  return (
    <div className="home help">
      <div className="top">
        <a className="back" href="?profile=1" aria-label="Back">‹</a>
        <h1 className="heroline">Help</h1>
      </div>

      <main>
        {/* ⚠️ THE LEAD SENDS PEOPLE TO ZARYNA FOR THE OTHER KIND OF QUESTION,
            and that division is the point: this page is about the account and
            the app, she is about where to eat. */}
        <p className="lead">
          Questions about your account and about Eleat. For somewhere to eat,
          Zaryna is in the bar at the bottom of every screen.
        </p>

        {SECTIONS.map(([heading, items]) => (
          <section key={heading} className="hsect">
            <div className="sect">{heading}</div>
            {items.map(([q, a]) => {
              const id = heading + q;
              const isOpen = open === id;
              return (
                <div key={q} className={isOpen ? 'faq on' : 'faq'}>
                  <button
                    type="button"
                    className="faqq"
                    aria-expanded={isOpen}
                    onClick={() => setOpen(isOpen ? null : id)}
                  >
                    <span>{q}</span>
                    {/* ⚠️ THE CHEVRON TURNS, so the control says which state it
                        is in rather than which state it wants. */}
                    <span className="faqc" aria-hidden="true">{isOpen ? '⌄' : '›'}</span>
                  </button>
                  {isOpen && <p className="faqa">{a}</p>}
                </div>
              );
            })}
          </section>
        ))}

        <div className="contact">Still need help?</div>
        {/* ⚠️ AND THE WAY OUT IS A REAL LINK, not a sentence about a link. The
            locked screen says she is in the bar at the bottom of every screen,
            and she is — but somebody reading this is already stuck, so it also
            goes straight there. */}
        <p className="cs">
          <a className="link" href="?zaryna=1">Ask Zaryna</a>. She is in the bar
          at the bottom of every screen and she knows the whole app.
        </p>
        {/* the one way to reach Eleat itself — Al-Noor, 14 Oct 2026 (the app's
            footer is gone; this is where Contact lives now) */}
        <p className="cs">Or email us at <a className="link" href="mailto:contact@eleat.app">contact@eleat.app</a>.</p>
      </main>

      <TabBar active="profile" />
    </div>
  );
}
